import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const CalendarPopup = ({ isOpen, onClose, selectedDate, onDateSelect, generationDates = [] }) => {
  const today = new Date();
  const [viewYear, setViewYear] = useState(selectedDate?.getFullYear() || today.getFullYear());
  const [viewMonth, setViewMonth] = useState(selectedDate?.getMonth() ?? today.getMonth());
  const [mode, setMode] = useState('days'); // 'days' | 'months' | 'years'

  // Build a Set of "YYYY-MM-DD" strings for quick lookup
  const generationDateSet = useMemo(() => {
    const set = new Set();
    generationDates.forEach((d) => {
      const date = new Date(d);
      if (!isNaN(date)) {
        set.add(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`);
      }
    });
    return set;
  }, [generationDates]);

  if (!isOpen) return null;

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();

  // Build weeks
  const weeks = [];
  let currentWeek = new Array(firstDayOfWeek).fill(null);
  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) currentWeek.push(null);
    weeks.push(currentWeek);
  }

  const hasGeneration = (day) => {
    if (!day) return false;
    const key = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return generationDateSet.has(key);
  };

  const isSelected = (day) => {
    if (!day || !selectedDate) return false;
    return (
      selectedDate.getFullYear() === viewYear &&
      selectedDate.getMonth() === viewMonth &&
      selectedDate.getDate() === day
    );
  };

  const isToday = (day) => {
    if (!day) return false;
    return (
      today.getFullYear() === viewYear &&
      today.getMonth() === viewMonth &&
      today.getDate() === day
    );
  };

  const handleDayClick = (day) => {
    if (!day) return;
    const date = new Date(viewYear, viewMonth, day);
    onDateSelect(date);
    onClose();
  };

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  // Year picker range
  const yearStart = Math.floor(viewYear / 12) * 12;
  const years = Array.from({ length: 12 }, (_, i) => yearStart + i);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/80"
        onClick={onClose}
      />
      {/* Calendar Popup */}
      <div className="absolute top-full left-0 mt-2 z-50 glassmorphic-card rounded-2xl p-4 border border-[rgba(254,131,56,0.2)] shadow-xl w-80">
        {/* Header with navigation */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => {
              if (mode === 'days') handlePrevMonth();
              else if (mode === 'months') setViewYear(viewYear - 1);
              else setViewYear(yearStart - 12);
            }}
            className="text-white/50 hover:text-white transition-colors p-1"
          >
            <Icon name="ChevronLeft" size={18} />
          </button>

          <button
            onClick={() => {
              if (mode === 'days') setMode('months');
              else if (mode === 'months') setMode('years');
              else setMode('months');
            }}
            className="text-white font-semibold text-base hover:text-orange-400 transition-colors"
          >
            {mode === 'days' && `${MONTH_NAMES[viewMonth]} ${viewYear}`}
            {mode === 'months' && `${viewYear}`}
            {mode === 'years' && `${yearStart} – ${yearStart + 11}`}
          </button>

          <div className="flex items-center gap-1">
            <button
              onClick={() => {
                if (mode === 'days') handleNextMonth();
                else if (mode === 'months') setViewYear(viewYear + 1);
                else setViewYear(yearStart + 12);
              }}
              className="text-white/50 hover:text-white transition-colors p-1"
            >
              <Icon name="ChevronRight" size={18} />
            </button>
            <button
              onClick={onClose}
              className="text-white/50 hover:text-white transition-colors p-1 ml-1"
            >
              <Icon name="X" size={18} />
            </button>
          </div>
        </div>

        {/* Year Picker */}
        {mode === 'years' && (
          <div className="grid grid-cols-3 gap-2">
            {years.map((year) => (
              <button
                key={year}
                onClick={() => {
                  setViewYear(year);
                  setMode('months');
                }}
                className={`py-2.5 rounded-xl text-sm font-medium transition-all ${
                  year === viewYear
                    ? 'bg-orange-500/20 text-white border border-orange-500/50'
                    : year === today.getFullYear()
                    ? 'bg-white/5 text-orange-400 hover:bg-white/10'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                }`}
              >
                {year}
              </button>
            ))}
          </div>
        )}

        {/* Month Picker */}
        {mode === 'months' && (
          <div className="grid grid-cols-3 gap-2">
            {MONTH_NAMES.map((month, idx) => (
              <button
                key={month}
                onClick={() => {
                  setViewMonth(idx);
                  setMode('days');
                }}
                className={`py-2.5 rounded-xl text-sm font-medium transition-all ${
                  idx === viewMonth
                    ? 'bg-orange-500/20 text-white border border-orange-500/50'
                    : idx === today.getMonth() && viewYear === today.getFullYear()
                    ? 'bg-white/5 text-orange-400 hover:bg-white/10'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                }`}
              >
                {month.slice(0, 3)}
              </button>
            ))}
          </div>
        )}

        {/* Day Picker */}
        {mode === 'days' && (
          <>
            {/* Weekday Headers */}
            <div className="grid grid-cols-7 gap-2 mb-2">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                <div key={day} className="text-center text-white/50 text-xs font-medium">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="space-y-2">
              {weeks.map((week, weekIdx) => (
                <div key={weekIdx} className="grid grid-cols-7 gap-2">
                  {week.map((day, dayIdx) => {
                    const generated = hasGeneration(day);
                    const selected = isSelected(day);
                    const todayDay = isToday(day);

                    return (
                      <button
                        key={dayIdx}
                        onClick={() => handleDayClick(day)}
                        disabled={!day}
                        className={`
                          aspect-square rounded-lg text-sm font-medium transition-all relative flex items-center justify-center
                          ${!day ? 'invisible' : ''}
                          ${selected
                            ? 'ring-2 ring-orange-500 bg-orange-500/20 text-white'
                            : generated
                            ? 'bg-orange-500/15 text-white border border-orange-500/40 hover:bg-orange-500/25'
                            : todayDay
                            ? 'bg-white/10 text-white hover:bg-white/15'
                            : 'bg-transparent text-white/30 hover:bg-white/5 hover:text-white/60'
                          }
                        `}
                      >
                        {day}
                        {generated && (
                          <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-orange-500 rounded-full" />
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 mt-4 pt-3 border-t border-white/5">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 bg-orange-500 rounded-full" />
                <span className="text-white/40 text-[10px]">Generation</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-white/10 border border-white/20" />
                <span className="text-white/40 text-[10px]">Today</span>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default CalendarPopup;
